import { cn } from "cn";
import type * as React from "react";
import { BwChevronDownLine, BwCloseCircleFill } from "@/components/ui/icons";

export type SelectTriggerProps = React.ComponentProps<"div"> & {
  /** When the bound value is empty, render the placeholder text color. */
  isEmpty?: boolean;
  disabled?: boolean;
  /**
   * A `SelectClearFrame` around this trigger draws an X over the chevron: the chevron gives way
   * to it on hover, on touch screens and while the X has focus.
   */
  hasClear?: boolean;
};

/**
 * The shared bordered `<div role="combobox">` trigger chrome, previously
 * duplicated verbatim across `UserSingleSelect` / `CategoriesSelect` /
 * `PlaceSelect`. Render the selected/placeholder content as children; the
 * chevron is appended automatically. Wrap in `<PopoverTrigger asChild>` so
 * Radix injects `data-state`, `aria-*`, and the ref.
 */
export function SelectTrigger({
  children,
  isEmpty,
  className,
  disabled,
  hasClear = false,
  ...props
}: SelectTriggerProps) {
  const isClearShown = hasClear && !isEmpty && !disabled;
  return (
    <div
      role="combobox"
      className={cn(
        /* sorted */
        "flex items-center justify-between",
        "w-full min-h-10",
        "px-3 py-2",
        "cursor-pointer rounded-lg outline outline-outline-dark-low",
        "transition-[color,box-shadow]",
        "aria-invalid:outline-outline-negative-max aria-invalid:data-[state=open]:outline-outline-negative-max data-[state=open]:outline-[1.5px] data-[state=open]:outline-outline-dark-max group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
        isEmpty && "text-text-dark-min",
        disabled && "cursor-default",
        className,
      )}
      {...props}
    >
      {children}
      {!disabled && (
        <BwChevronDownLine
          size={16}
          className={cn(
            /* sorted */
            "shrink-0",
            "text-text-dark-high",
            isClearShown &&
              "group-hover/select-clear:opacity-0 group-has-[[data-select-clear]:focus-visible]/select-clear:opacity-0",
            isClearShown && "pointer-coarse:opacity-0",
          )}
        />
      )}
    </div>
  );
}

/**
 * A select trigger with an X that clears it. The X is the trigger's SIBLING, laid over the
 * chevron, not its child: a button inside the `role="combobox"` trigger would be a nested
 * interactive control that screen readers flatten away. It shows on hover, on touch screens
 * (no hover there, so always) and on keyboard focus, and its tap area is 32px around the 16px
 * icon. Renders `children` alone when `onClear` is not set.
 */
export function SelectClearFrame({
  children,
  onClear,
  clearLabel = "Clear",
  isEmpty,
  disabled,
}: {
  children: React.ReactNode;
  onClear?: () => void;
  clearLabel?: string;
  isEmpty?: boolean;
  disabled?: boolean;
}) {
  if (!onClear) return <>{children}</>;
  return (
    <div className="group/select-clear relative w-full">
      {children}
      {!isEmpty && !disabled && (
        <button
          type="button"
          data-select-clear
          aria-label={clearLabel}
          onClick={onClear}
          className={cn(
            /* sorted */
            "absolute top-1/2 right-1 flex items-center justify-center",
            "size-8",
            "text-text-dark-min",
            "rounded-full cursor-pointer opacity-0",
            "-translate-y-1/2",
            "group-hover/select-clear:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary",
            "pointer-coarse:opacity-100",
          )}
        >
          <BwCloseCircleFill size={16} />
        </button>
      )}
    </div>
  );
}
